import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

export interface User {
  id: string;
  username: string;
  fullName: string;
  isMain: boolean;
  createdAt?: string;
  roleId?: string | null;
  role?: { id: string; name: string } | null;
  /** Absent when talking to a pre-RBAC backend — treat as "everything". */
  permissions?: string[];
}

export interface PermissionDef { key: string; label: string; group: string; requires?: string }
export interface RoleDto {
  id: string; name: string; description: string; permissions: string[]; isSystem: boolean; userCount: number;
}

export interface LoginResponse {
  success: boolean;
  token?: string;
  user?: User;
  error?: string;
}

export interface UsersResponse {
  success: boolean;
  users: User[];
}

// Get token from localStorage
export const getToken = (): string | null => {
  return localStorage.getItem('fleet_token');
};

// Set token in localStorage
export const setToken = (token: string): void => {
  localStorage.setItem('fleet_token', token);
};

// Remove token from localStorage
export const removeToken = (): void => {
  localStorage.removeItem('fleet_token');
};

// Get stored user from localStorage
export const getStoredUser = (): User | null => {
  const userStr = localStorage.getItem('fleet_user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
};

// Set user in localStorage
export const setStoredUser = (user: User): void => {
  localStorage.setItem('fleet_user', JSON.stringify(user));
};

// Remove user from localStorage
export const removeStoredUser = (): void => {
  localStorage.removeItem('fleet_user');
};

// Create axios instance with auth header
const createAuthAxios = () => {
  const token = getToken();
  return axios.create({
    baseURL: API_BASE_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

// Login
export const login = async (username: string, password: string): Promise<LoginResponse> => {
  const response = await axios.post<LoginResponse>(`${API_BASE_URL}/auth/login`, {
    username,
    password,
  });
  return response.data;
};

// Get current user
export const getCurrentUser = async (): Promise<User | null> => {
  try {
    const api = createAuthAxios();
    const response = await api.get<{ success: boolean; user: User }>('/auth/me');
    return response.data.user;
  } catch {
    return null;
  }
};

// Get all users
export const getUsers = async (): Promise<User[]> => {
  const api = createAuthAxios();
  const response = await api.get<UsersResponse>('/auth/users');
  return response.data.users;
};

// Create user
export const createUser = async (
  username: string,
  password: string,
  fullName: string,
  roleId?: string
): Promise<{ success: boolean; user?: User; error?: string }> => {
  const api = createAuthAxios();
  const response = await api.post('/auth/users', { username, password, fullName, roleId });
  return response.data;
};

// Update user
export const updateUser = async (
  userId: string,
  updates: { fullName?: string; password?: string; roleId?: string }
): Promise<{ success: boolean; user?: User; error?: string }> => {
  const api = createAuthAxios();
  const response = await api.put(`/auth/users/${userId}`, updates);
  return response.data;
};

// Delete user
export const deleteUser = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  const api = createAuthAxios();
  const response = await api.delete(`/auth/users/${userId}`);
  return response.data;
};

// Change password
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> => {
  const api = createAuthAxios();
  const response = await api.put('/auth/change-password', { currentPassword, newPassword });
  return response.data;
};

// Logout
export const logout = (): void => {
  removeToken();
  removeStoredUser();
};

export const getPermissionCatalog = async (): Promise<PermissionDef[]> =>
  (await createAuthAxios().get<{ permissions: PermissionDef[] }>('/auth/permissions')).data.permissions;

export const getRoles = async (): Promise<RoleDto[]> =>
  (await createAuthAxios().get<{ roles: RoleDto[] }>('/auth/roles')).data.roles;

export const createRole = async (input: { name: string; description?: string; permissions: string[] }): Promise<RoleDto> =>
  (await createAuthAxios().post<{ role: RoleDto }>('/auth/roles', input)).data.role;

export const updateRole = async (id: string, input: { name?: string; description?: string; permissions?: string[] }): Promise<RoleDto> =>
  (await createAuthAxios().put<{ role: RoleDto }>(`/auth/roles/${id}`, input)).data.role;

export const deleteRole = async (id: string): Promise<void> => {
  await createAuthAxios().delete(`/auth/roles/${id}`);
};
